import React, { useState, useRef, useEffect, createElement } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Nav,
  NavItem,
  TabContent,
  TabPane,
  NavLink,
  Button,
  Input,
  FormGroup,
  Label,
  Container,
  Badge,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomModal from "./CustomModal";
import Breadcrumbs from "components/Common/BreadCrumbs";
import { showAlert } from "components/Common/commonFunctions";
import { customStyle, MAX_FILE_SIZE, CREATED_BY } from "components/Common/constants";
import Select from "react-select";
import { getproductbyID, editProduct } from "actions/product";
import { getProductCategoryList } from "actions/productCategories";
import { getManufacturerList } from "actions/manufacturer";
import NotificationAlert from "react-notification-alert";
import { urlDecodedId } from "components/Common/encodeDecodeFunction";
import { downloadProductManual } from "actions/product";

const initialFields = {
  name: {
    value: "",
    error: "",
  },
  category: {
    value: null,
    error: "",
  },
  manufacturer: {
    value: null,
    error: "",
  },
  modelNo: {
    value: "",
    error: "",
  },
  createdBy: {
    value: null,
    error: "",
  },
  images: {
    value: [],
    error: "",
  },
  documents: {
    value: [{ title: "", file: null, name: "", error: "" }],
    error: "",
  },
};
function ProductDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const notificationAlertRef = useRef(null);

  const productCategoryListData = useSelector(
    (state) => state.productCategory.productCategoryList,
  );
  const manufacturerListData = useSelector(
    (state) => state.manufacturer.manufacturerList,
  );
  const [activeTab, setActiveTab] = useState("details");
  const queryParams = new URLSearchParams(location.search);
  const isEditFromUrl = queryParams.get("edit") === "true";
  const [isEditing, setIsEditing] = useState(isEditFromUrl);
  const [productFormData, setProductFormData] = useState(initialFields);
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    error: "",
  });
  //state to store deleted document
  const [deletedDocs, setDeletedDocs] = useState([]);

  const [isDownloading, setIsDownloading] = useState(false);

  //decode id here from url
  const decodedId = id ? urlDecodedId(id) : null;
  //to get product details
  useEffect(() => {
    if (decodedId) {
      getProductDetails(decodedId);
    }
  }, [decodedId]);

  // to get product category & manufacturer list
  useEffect(() => {
    const filterJSON = [
      {
        field: "Status",
        condition: "eq",
        filterValues: ["ACTIVE"],
      },
    ];
    getProductCategoryList(dispatch);
    getManufacturerList(dispatch, { filters: JSON.stringify(filterJSON) });
  }, []);

  //get product details using product id
  const getProductDetails = async (id) => {
    try {
      setShowAlternative({ loader: true, error: false });
      const res = await getproductbyID(id);

      if (res.code === 200) {
        const productDetails = res.data;
        setProduct(productDetails);
        setProductFormData({
          name: { value: productDetails.name || "", error: "" },
          modelNo: { value: productDetails.modelNo || "", error: "" },
          manufacturer: {
            value: productDetails.manufacturer
              ? {
                label: productDetails.manufacturer.name,
                value: productDetails.manufacturer.ID,
              }
              : null,
            error: "",
          },
          category: {
            value: productDetails.productCategory
              ? {
                label: productDetails.productCategory.category,
                value: productDetails.productCategory.ID,
              }
              : null,
            error: "",
          },
          createdBy: {
            value: productDetails.createdBy,
            error: "",
          },
          images: {
            value: productDetails.imagePath
              ? [{ url: productDetails.imagePath, file: null }]
              : [],
            error: "",
          },
          documents: {
            value:
              productDetails.productDocuments?.map((doc) => ({
                id: doc.id,
                title: doc.document_title,
                url: doc.document_path,
                name: doc.document_path.split("/").pop(),
                file: null,
              })) || [],
            error: "",
          },
        });

        setShowAlternative({ loader: false, error: "" });
      } else {
        setShowAlternative({
          loader: false,
          error: res.message || "Failed to load product details",
        });
      }
    } catch (err) {
      setShowAlternative({
        loader: false,
        error: "Something went wrong loading product details",
      });
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
    setIsEditing(false);
  };

  const handleOnEditClick = () => {
    setIsEditing(true);
    // Update URL query param in place (no reload)
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("edit", "true");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });

    setProductFormData({
      name: { value: product.name || "", error: "" },
      modelNo: { value: product.modelNo || "", error: "" },
      manufacturer: {
        value: product.manufacturer
          ? { value: product.manufacturer.ID, label: product.manufacturer.name }
          : null,
        error: "",
      },
      category: {
        value: product.productCategory
          ? {
            value: product.productCategory.ID,
            label: product.productCategory.category,
          }
          : null,
        error: "",
      },
      createdBy: {
        value: product.createdBy || "",
        error: "",
      },
      images: {
        value: product.imagePath
          ? [{ url: product.imagePath, file: null }]
          : [],
        error: "",
      },
      documents: {
        value:
          product.productDocuments?.map((doc) => ({
            id: doc.id,
            title: doc.document_title,
            url: doc.document_path,
            name: doc.document_path.split("/").pop(),
            file: null,
          })) || [],
        error: "",
      },
    });
  };

  const handleCancel = () => {
    setIsEditing(false);

    setProductFormData({
      name: { value: product.name || "", error: "" },
      modelNo: { value: product.modelNo || "", error: "" },
      manufacturer: {
        value: product.manufacturer
          ? { value: product.manufacturer.ID, label: product.manufacturer.name }
          : null,
        error: "",
      },
      category: {
        value: product.productCategory
          ? {
            value: product.productCategory.ID,
            label: product.productCategory.category,
          }
          : null,
        error: "",
      },
      createdBy: {
        value: product.createdBy || "",
        error: "",
      },
      images: {
        value: product.imagePath
          ? [{ url: product.imagePath, file: null }]
          : [],
        error: "",
      },
      documents: {
        value:
          product.productDocuments?.map((doc) => ({
            id: doc.id,
            title: doc.document_title,
            url: doc.document_path,
            name: doc.document_path.split("/").pop(),
            file: null,
          })) || [],
        error: "",
      },
    });
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("edit");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const fetchCategoryByManufacturer = (manufacturerID) => {
    if (!manufacturerID) {
      getProductCategoryList(dispatch);
      return;
    }

    const filterJSON = JSON.stringify([
      {
        field: "ManufacturerID",
        condition: "eq",
        filterValues: [String(manufacturerID)],
      },
    ]);

    getProductCategoryList(dispatch, { filters: filterJSON });
  };

  const handleInputChange = (name, value) => {
    if (name === "manufacturer") {
      setProductFormData((prev) => ({
        ...prev,
        manufacturer: {
          ...prev.manufacturer,
          value,
        },
        category: {
          value: null,
          error: "",
        },
      }));

      fetchCategoryByManufacturer(value?.value);
      return;
    }

    setProductFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
      },
    }));
  };

  const validateForm = () => {
    let product = { ...productFormData };
    let isValid = true;

    Object.keys(product).forEach((key) => {
      if (key === "documents") {
        const docs = product[key].value || [];

        docs.forEach((doc) => {
          const errors = getValidationError("document", doc);

          if (errors.titleError || errors.fileError) {
            isValid = false;
          }

          doc.titleError = errors.titleError;
          doc.fileError = errors.fileError;
        });

        product[key] = {
          ...product[key],
          value: docs,
        };
      } else {
        const error = getValidationError(key, product[key].value);

        product[key] = {
          ...product[key],
          error,
        };

        if (error) {
          isValid = false;
        }
      }
    });

    setProductFormData(product);
    return isValid;
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "name":
        return !value ? "Please enter a product name" : "";
      case "manufacturer":
        return !value ? "Please select a manufacturer" : "";
      case "category":
        return !value ? "Please select a category" : "";
      case "modelNo":
        return !value ? "Please enter a model number" : "";
      case "createdBy":
        return !value ? "Please select created by" : "";
      case "document":
        return {
          titleError:
            (value?.file || value?.url) && !value?.title?.trim()
              ? "Please enter document title"
              : "",

          fileError:
            value?.title?.trim() && !value?.file && !value?.url ? "Please upload PDF" : "",
        };
      default:
        return "";
    }
  };
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setProductFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleDocumentBlur = (index) => {
    setProductFormData((prev) => {
      const updatedDocs = [...(prev.documents.value || [])];

      if (!updatedDocs[index]) {
        updatedDocs[index] = { title: "", file: null, name: "" };
      }

      const errors = getValidationError("document", updatedDocs[index]);

      updatedDocs[index].titleError = errors.titleError;
      updatedDocs[index].fileError = errors.fileError;

      return {
        ...prev,
        documents: {
          ...prev.documents,
          value: updatedDocs,
        },
      };
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setShowLoaderOnClick(true);

    try {
      const formData = new FormData();
      // Product data
      const productData = {
        manufacturerID: productFormData.manufacturer.value?.value,
        productCategoryID: productFormData.category.value?.value,
        name: productFormData.name.value,
        modelNo: productFormData.modelNo.value,
        description: productFormData.description?.value || "",
        createdBy: productFormData.createdBy.value?.value,
        status: productFormData.status?.value || product?.status,
      };
      formData.append("product", JSON.stringify(productData));

      // Product image
      const imageFile = productFormData.images.value[0]?.file;
      if (imageFile) formData.append("isImage", imageFile);

      // Documents
      const docs = productFormData.documents.value || [];
      if (docs.length > 5) {
        notify("danger", "You can attach a maximum of 5 documents.");
        setShowLoaderOnClick(false);
        return;
      }
      //deleted any document then send key as document id and value as "isDeleted"
      deletedDocs.forEach((doc) => {
        if (doc.id) {
          formData.append(doc.id.toString(), "isDeleted");
        }
      });

      docs.forEach((doc) => {
        // New document
        if (!doc.id && doc.file) {
          formData.append(doc.title, doc.file);
          return;
        }
        // Existing document
        if (doc.id) {
          const originalDoc = product?.productDocuments?.find(
            (d) => d.id === doc.id,
          );

          const isTitleChanged =
            originalDoc && originalDoc.document_title !== doc.title;

          // Only title changed and file is not added ->send as key and title as value
          if (!doc.file && isTitleChanged) {
            formData.append(doc.id.toString(), doc.title);
          }
          // file is changed → add that as new doc and its id is send as isDeleted(which is already sent above)
          if (deletedDocs.some((d) => d.id === doc.id)) {
            formData.append(doc.title, doc.file);
          }
        }
      });

      const res = await editProduct(decodedId, formData);
      if (res.code === 200) {
        notify(
          "success",
          res?.message || "Product details updated successfully",
        );

        await getProductDetails(decodedId);
        setIsEditing(false);
        setDeletedDocs([]);
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete("edit");
        navigate(`${location.pathname}?${searchParams.toString()}`, {
          replace: true,
        });
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to update product!";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Failed to edit product data");
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const handleChangeStatus = async (newStatus) => {
    try {
      setShowLoaderOnClick(true);

      const formdata = new FormData();
      const updatedStatus = {
        status: newStatus,
      };
      formdata.append("product", JSON.stringify(updatedStatus));
      const res = await editProduct(decodedId, formdata);
      if (res.code === 200) {
        notify("success", res.message || "Product status updated");
        await getProductDetails(decodedId);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to update status!";
        notify("danger", message);
      }
    } catch (e) {
      notify("danger", "Failed to update status!");
    }
  };

  // Status Change Modal
  const StatusChangeModal = () => {
    showAlert({
      title: `Are you sure you want to ${product?.status === "ACTIVE" ? "deactivated" : "activate"
        } this product?`,
      message: `Product "${product?.name}" will be ${product?.status === "ACTIVE" ? "deactivated" : "activated"
        }.`,
      type: "warning",
      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => {
        handleChangeStatus(
          product?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
        );
      },
      onCancel: () => { },
    });
  };

  const handleTitleChange = (index, value) => {
    setProductFormData((prev) => {
      const updatedDocs = [...(prev.documents.value || [])];
      const doc = updatedDocs[index] || {};

      updatedDocs[index] = {
        ...doc,
        title: value,
        file: doc.file || null,
        name: doc.name || "",
      };

      if (doc.titleError || doc.fileError) {
        const errors = getValidationError("document", updatedDocs[index]);
        updatedDocs[index].titleError = errors.titleError;
        updatedDocs[index].fileError = errors.fileError;
      }

      return {
        ...prev,
        documents: {
          ...prev.documents,
          value: updatedDocs,
        },
      };
    });
  };

  const handleFileChange = (index, file) => {
    setProductFormData((prev) => {
      const updatedDocs = [...(prev.documents.value || [])];
      // If the document at this index doesn't exist, create it
      if (!updatedDocs[index]) {
        updatedDocs[index] = { title: "", file: null, name: "" };
      }
      if (file) {
        updatedDocs[index].file = file;
        updatedDocs[index].name = file.name;
      }
      return {
        ...prev,
        documents: {
          ...prev.documents,
          value: updatedDocs,
        },
      };
    });
  };

  // remove document on X click
  const handleRemoveDoc = (index) => {
    const docs = [...productFormData.documents.value];

    if (docs[index].id) {
      const removed = docs.splice(index, 1);
      setDeletedDocs((prev) => [...prev, removed[0]]);
    } else {
      docs.splice(index, 1);
    }

    handleInputChange("documents", docs);
  };

  //to add new document while editing
  const handleAddDoc = () => {
    setProductFormData((prev) => ({
      ...prev,
      documents: {
        value: [...prev.documents.value, { title: "", file: null }],
        error: "",
      },
    }));
  };


  const handleDownloadDocument = async (productDocumentID, type) => {
    setIsDownloading(true);
    try {
      const res = await downloadProductManual(productDocumentID, type);
      if (res.status === 200 && res.data) {
        const fileName =
          res.headers["content-disposition"]?.match(/filename="(.+?)"/)?.[1]
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      } else if (res.code === 404) {
        notify(
          "danger",
          res?.message || "Manual not found.",
        );
      } else {
        notify(
          "danger",
          res?.message || "Failed to download manual. Please try again.",
        );
      }
    } catch (error) {
      notify(
        "danger",
        error?.message || "Failed to download manual. Please try again.",
      );
    }
    setIsDownloading(false);
  };

  return (
    <Container fluid className="mt-4">
      {isDownloading && (
        <div className="loading-overlay">
          <div className="loading-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Downloading… Please wait.
            </div>
          </div>
        </div>
      )}
      <NotificationAlert ref={notificationAlertRef} />
      <Breadcrumbs
        items={[
          { name: "Products", link: "/admin/products" },
          { name: productFormData?.name?.value || "" },
        ]}
      />
      <Row>
        <Card className="m-3 shadow-sm border-0">
          {showAlternative.loader ? (
            <div className="loaderContainer">
              <i className="fa fa-spinner fa-spin fa-2xl loader" />
            </div>
          ) : showAlternative.error ? (
            <div className="loaderContainer text-muted d-flex flex-column">
              <h4 className="text-muted">No Product Data Available</h4>
              <span className="text-danger">{showAlternative.error}</span>
            </div>
          ) : (
            <>
              <Nav className="custom-tabs">
                <NavItem>
                  <NavLink
                    className={`text-center ${activeTab === "details" ? "active" : ""
                      }`}
                    href="#"
                    onClick={() => toggleTab("details")}
                  >
                    Product Details
                  </NavLink>
                </NavItem>
              </Nav>

              <CardBody>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="details">
                    <Row className="justify-content-center">
                      <Col md={12} lg={12}>
                        {!isEditing && (
                          <Row className="d-flex justify-content-end mr-2 mb-1">
                            <Button
                              color="default"
                              size="sm"
                              onClick={handleOnEditClick}
                            >
                              <i className="fa fa-pencil mr-2" />
                              Edit
                            </Button>
                            <Button
                              color="secondary"
                              size="sm"
                              className="ml-3"
                              onClick={() => StatusChangeModal()}
                            >
                              <i
                                className={`fa ${product?.status === "ACTIVE"
                                  ? "fa-user-slash"
                                  : "fa-user"
                                  } mr-2`}
                              />

                              {product?.status === "ACTIVE"
                                ? "Inactive"
                                : "Activate"}
                            </Button>
                          </Row>
                        )}

                        <Row className="px-3 mt-2">
                          <Col md={4} className="mb-2">
                            <FormGroup className="d-flex flex-column align-items-start mt-2">
                              <Label>
                                Product Name{" "}
                                {isEditing && (
                                  <span className="validation-error">*</span>
                                )}
                              </Label>
                              {isEditing ? (
                                <>
                                  <Input
                                    name="name"
                                    type="text"
                                    placeholder="Enter product name"
                                    value={productFormData.name.value}
                                    onChange={(e) =>
                                      handleInputChange(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleOnBlur(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    maxLength={100}
                                    required
                                  />
                                  {productFormData.name.error && (
                                    <small className="validation-error">
                                      {productFormData.name.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text text-wrap">
                                  {product?.name}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-2">
                            <FormGroup className="d-flex flex-column align-items-start mt-2">
                              <Label>
                                Manufacturer
                                {isEditing && (
                                  <span className="validation-error">*</span>
                                )}
                              </Label>
                              {isEditing ? (
                                <>
                                  <Select
                                    className="text-left"
                                    options={
                                      manufacturerListData?.data?.manufacturer?.map(
                                        (m) => ({
                                          value: m.ID,
                                          label: m.name,
                                        }),
                                      ) || []
                                    }
                                    placeholder="Select manufacturer"
                                    classNamePrefix="react-select"
                                    styles={customStyle}
                                    value={productFormData.manufacturer.value}
                                    onChange={(selectedOption) =>
                                      handleInputChange(
                                        "manufacturer",
                                        selectedOption,
                                      )
                                    }
                                    onBlur={() =>
                                      handleOnBlur(
                                        "manufacturer",
                                        productFormData.manufacturer.value,
                                      )
                                    }
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                  />

                                  {productFormData.manufacturer.error && (
                                    <small className="validation-error">
                                      {productFormData.manufacturer.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text">
                                  {product?.manufacturer?.name}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-2">
                            <FormGroup className="d-flex flex-column align-items-start mt-2">
                              <Label>
                                Category{" "}
                                {isEditing && (
                                  <span className="validation-error">*</span>
                                )}
                              </Label>
                              {isEditing ? (
                                <>
                                  <Select
                                    className="text-left"
                                    options={
                                      productCategoryListData?.data?.productCategory?.map(
                                        (c) => ({
                                          value: c.ID,
                                          label: c.category,
                                        }),
                                      ) || []
                                    }
                                    placeholder="Select category"
                                    classNamePrefix="react-select"
                                    styles={customStyle}
                                    value={productFormData.category.value}
                                    isDisabled={!productFormData.manufacturer.value}
                                    onChange={(selectedOption) =>
                                      handleInputChange(
                                        "category",
                                        selectedOption,
                                      )
                                    }
                                    onBlur={() =>
                                      handleOnBlur(
                                        "category",
                                        productFormData.category.value,
                                      )
                                    }
                                  />
                                  {productFormData.category.error && (
                                    <small className="validation-error">
                                      {productFormData.category.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text">
                                  {product?.productCategory?.category}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-2">
                            <FormGroup className="d-flex flex-column align-items-start mt-2">
                              <Label>
                                Model Number
                                {isEditing && (
                                  <span className="validation-error">*</span>
                                )}
                              </Label>
                              {isEditing ? (
                                <>
                                  <Input
                                    name="modelNo"
                                    type="text"
                                    placeholder="Enter model number"
                                    autoComplete="off"
                                    value={productFormData.modelNo.value}
                                    onChange={(e) =>
                                      handleInputChange(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleOnBlur(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    maxLength={100}
                                    required
                                  />
                                  {productFormData.modelNo.error && (
                                    <small className="validation-error">
                                      {productFormData.modelNo.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text text-wrap">
                                  {product?.modelNo}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-2">
                            <FormGroup className="d-flex flex-column align-items-start mt-2">
                              <Label>
                                Created By
                                {isEditing && (
                                  <span className="validation-error">*</span>
                                )}
                              </Label>

                              {isEditing ? (
                                <>
                                  <Select
                                    className="text-left"
                                    options={CREATED_BY}
                                    placeholder="Select created by"
                                    classNamePrefix="react-select"
                                    styles={customStyle}
                                    value={CREATED_BY.find(
                                      (item) =>
                                        item.value === productFormData.createdBy.value,
                                    )}
                                    onChange={(selectedOption) =>
                                      handleInputChange(
                                        "createdBy",
                                        selectedOption,
                                      )
                                    }
                                    onBlur={() =>
                                      handleOnBlur(
                                        "createdBy",
                                        productFormData.createdBy.value,
                                      )
                                    }
                                  />

                                  {productFormData.createdBy.error && (
                                    <small className="validation-error">
                                      {productFormData.createdBy.error}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <span className="small-text">
                                  {CREATED_BY.find((item) => item.value === product?.createdBy)?.label || "-"}
                                </span>
                              )}
                            </FormGroup>
                          </Col>

                          <Col md={4} className="mb-0">
                            <FormGroup className="d-flex flex-column align-items-start mt-2">
                              <Label>Status</Label>
                              <span>
                                <Badge
                                  className="px-3 py-2"
                                  color={
                                    product?.status === "ACTIVE"
                                      ? "success"
                                      : "warning"
                                  }
                                  pill
                                >
                                  {product?.status}
                                </Badge>
                              </span>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row className="px-3 mt-4">
                          {/* Product Images */}
                          <Col md={4}>
                            <Label className="fw-bold mb-3">
                              Product Image
                              {/* <span className="validation-error">*</span> */}
                              {isEditing && (
                                <span className="text-muted ml-2">
                                  (Accepted image formats: PNG, JPEG, JPG.
                                  Maximum file size: 10 MB.)
                                </span>
                              )}
                            </Label>

                            {isEditing ? (
                              <div
                                className="product-image-upload w-100 p-3 border rounded text-center"
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  const file = e.dataTransfer.files[0];
                                  if (file && file.type.startsWith("image/")) {
                                    handleInputChange("images", [
                                      {
                                        file,
                                        url: URL.createObjectURL(file),
                                      },
                                    ]);
                                  }
                                }}
                              >
                                {productFormData.images?.value?.[0]?.url ? (
                                  <div className=" d-flex flex-wrap justify-content-start">
                                    <div className="image-preview-wrapper">
                                      <img
                                        src={
                                          productFormData.images.value[0].url
                                        }
                                        alt="Product"
                                        className="img-fluid rounded product-preview-img"
                                      />

                                      <span
                                        className="remove-image"
                                        onClick={() =>
                                          handleInputChange("images", [])
                                        }
                                      >
                                        &times;
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <i className="fa fa-cloud-upload fa-2x text-muted mb-2"></i>
                                    <p className="mb-1 text-muted">
                                      Click or drag image to upload
                                    </p>
                                    <Label
                                      for="productImageInput"
                                      className="btn btn-simple btn-sm mt-2"
                                    >
                                      Choose Image
                                    </Label>
                                  </>
                                )}

                                <Input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file.size > MAX_FILE_SIZE) {
                                      // 10 MB limit
                                      notify(
                                        "danger",
                                        "Image size should not exceed 10 MB",
                                      );
                                      e.target.value = ""; // reset input
                                      return;
                                    }
                                    if (file) {
                                      handleInputChange("images", [
                                        {
                                          file,
                                          url: URL.createObjectURL(file),
                                        },
                                      ]);
                                    }
                                    e.target.value = "";
                                  }}
                                  className="d-none"
                                  id="productImageInput"
                                />
                              </div>
                            ) : (productFormData.images?.value || []).length ===
                              0 ? (
                              <div className="text-muted text-center p-3 border rounded">
                                No image attached
                              </div>
                            ) : (
                              <div className="media-grid">
                                {productFormData.images?.value?.[0]?.url && (
                                  <div
                                    className="media-card"
                                    onClick={() =>
                                      setSelectedImage(
                                        productFormData.images.value[0].url,
                                      )
                                    }
                                  >
                                    <div className="media-preview-wrapper">
                                      <img
                                        src={
                                          productFormData?.images?.value[0].url
                                        }
                                        alt="product"
                                        className="media-preview"
                                      />
                                    </div>

                                    <div className="media-info">
                                      <div className="media-title text-truncate">
                                        Product Image
                                      </div>

                                      <div
                                        className="media-actions"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <span
                                          className="link-action view-link"
                                          onClick={() =>
                                            setSelectedImage(
                                              productFormData.images.value[0]
                                                .url,
                                            )
                                          }
                                        >
                                          <i className="fa fa-eye mr-1" /> View
                                        </span>

                                        <span
                                          className="link-action download-link"
                                          onClick={() =>
                                            handleDownloadDocument(
                                              decodedId,
                                              "image",
                                            )
                                          }
                                        >
                                          <i className="fa fa-download mr-1" />{" "}
                                          Download
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </Col>

                          {/* PDF Documents */}
                          <Col md={8}>
                            <Label className="fw-bold mb-3">
                              Attached Documents
                              {/* <span className="validation-error">*</span> */}
                              {isEditing && (
                                <span className="text-muted ml-2">
                                  (Maximum size allowed per pdf : 10Mb)
                                </span>
                              )}
                            </Label>

                            {isEditing ? (
                              <div className="document-section w-100 p-3 rounded border">
                                {(productFormData.documents?.value?.length
                                  ? productFormData.documents.value
                                  : [{ title: "", file: null, name: "" }]
                                ).map((doc, index) => {
                                  const totalDocs =
                                    productFormData.documents?.value?.length ||
                                    0;

                                  return (
                                    <Row
                                      key={doc?.id?.toString() || index}
                                      className="mb-2 p-0"
                                    >
                                      <Col xs="12" md="5">
                                        <FormGroup>
                                          <Label className="form-label text-muted small mb-1">
                                            Document Title
                                          </Label>
                                          <Input
                                            type="text"
                                            placeholder="Enter document title"
                                            value={doc.title || ""}
                                            onChange={(e) =>
                                              handleTitleChange(
                                                index,
                                                e.target.value,
                                              )
                                            }
                                            maxLength={100}
                                            onBlur={() =>
                                              handleDocumentBlur(index)
                                            }
                                          />
                                          {doc.titleError && (
                                            <small className="validation-error">
                                              {doc.titleError}
                                            </small>
                                          )}
                                        </FormGroup>
                                      </Col>

                                      <Col xs="12" md="5">
                                        <FormGroup>
                                          <Label className="form-label text-muted small mb-1">
                                            Upload PDF
                                          </Label>
                                          <div className="custom-file-wrapper">
                                            <Input
                                              type="file"
                                              accept="application/pdf"
                                              className="custom-file-input"
                                              id={`documentFile-${index}`}
                                              onBlur={() =>
                                                handleDocumentBlur(index)
                                              }
                                              onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                  if (
                                                    file.size > MAX_FILE_SIZE
                                                  ) {
                                                    notify(
                                                      "danger",
                                                      "Document size should not exceed 10 MB",
                                                    );
                                                    e.target.value = ""; // reset input
                                                    return;
                                                  }
                                                  handleFileChange(index, file);
                                                  if (doc?.id) {
                                                    setDeletedDocs((prev) => [
                                                      ...prev,
                                                      doc,
                                                    ]);
                                                  }
                                                }
                                                e.target.value = ""; // allow reselecting same file
                                              }}
                                            />
                                            {doc.fileError && (
                                              <small className="validation-error">
                                                {doc.fileError}
                                              </small>
                                            )}
                                            <label
                                              htmlFor={`documentFile-${index}`}
                                              className={`custom-file-label ${doc?.name ? "has-file" : ""
                                                }`}
                                            >
                                              <span className="file-name">
                                                {doc?.name || "Choose PDF"}
                                              </span>
                                            </label>
                                          </div>
                                        </FormGroup>
                                      </Col>

                                      <Col
                                        xs="12"
                                        md="2"
                                        className="d-flex align-items-center mt-md-1 mt-0"
                                      >
                                        {totalDocs >= 1 && (
                                          <Button
                                            color="default"
                                            size="sm"
                                            className="btn-icon btn-simple mr-2"
                                            onClick={() =>
                                              handleRemoveDoc(index)
                                            }
                                          >
                                            <i className="fa fa-times" />
                                          </Button>
                                        )}
                                        {(index === totalDocs - 1 ||
                                          totalDocs === 0) && (
                                            <Button
                                              color="default"
                                              size="sm"
                                              className="btn-icon"
                                              onClick={handleAddDoc}
                                              disabled={totalDocs >= 5}
                                            >
                                              <i className="fa fa-plus" />
                                            </Button>
                                          )}
                                      </Col>
                                    </Row>
                                  );
                                })}
                              </div>
                            ) : (productFormData.documents?.value || [])
                              .length === 0 ? (
                              <div className="text-muted text-center p-3 border rounded">
                                No documents attached
                              </div>
                            ) : (
                              <div className="media-grid">
                                {(productFormData.documents?.value || []).map(
                                  (doc, index) => (
                                    <div
                                      key={index}
                                      className="media-card"
                                      onClick={() => setSelectedPDF(doc)}
                                    >
                                      <div className="media-preview-wrapper pdf-wrapper">
                                        <iframe
                                          src={`${doc.url}`}
                                          title={doc.title}
                                          className="pdf-preview"
                                        ></iframe>
                                      </div>
                                      <div className="media-info">
                                        <div className="media-title text-truncate">
                                          {doc.title}
                                        </div>
                                        <div
                                          className="media-actions"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <span
                                            className="link-action view-link"
                                            onClick={() => setSelectedPDF(doc)}
                                          >
                                            <i className="fa fa-eye mr-1" />{" "}
                                            View
                                          </span>
                                          <span
                                            className="link-action download-link"
                                            onClick={() =>
                                              handleDownloadDocument(
                                                doc?.id,
                                                "document",
                                              )
                                            }
                                          >
                                            <i className="fa fa-download mr-1" />{" "}
                                            Download
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                          </Col>
                        </Row>
                        {/* PDF Modal */}
                        <CustomModal
                          isOpen={!!selectedPDF}
                          onClose={() => setSelectedPDF(null)}
                          title={selectedPDF?.title}
                        >
                          <div className="pdf-container">
                            <iframe
                              src={selectedPDF?.url}
                              title="PDF Viewer"
                              className="pdf-iframe"
                            />
                          </div>
                        </CustomModal>

                        {/* Image Modal */}
                        <CustomModal
                          isOpen={!!selectedImage}
                          onClose={() => setSelectedImage(null)}
                          title="Image Preview"
                        >
                          <div className="image-container">
                            <img
                              src={selectedImage}
                              alt="Full preview"
                              className="image-preview"
                            />
                          </div>
                        </CustomModal>

                        <Row>
                          <Col className="d-flex justify-content-end">
                            {isEditing && (
                              <span className="mt-5 px-3">
                                <Button
                                  className="mr-2"
                                  color="secondary"
                                  size="sm"
                                  onClick={handleCancel}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  color="default"
                                  size="sm"
                                  onClick={() => {
                                    handleSave();
                                  }}
                                  disabled={
                                    Object.values(productFormData).some(
                                      (item) => item.error !== "",
                                    ) ||
                                    productFormData.documents.value.some(
                                      (doc) => doc.titleError || doc.fileError,
                                    )
                                  }
                                >
                                  {showLoaderOnClick ? (
                                    <i className="fa fa-spinner fa-spin fa-lg text-white" />
                                  ) : (
                                    "Save"
                                  )}
                                </Button>
                              </span>
                            )}
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </CardBody>
            </>
          )}
        </Card>
      </Row>
    </Container>
  );
}

export default ProductDetails;
